import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import {
  Factory,
  FileBarChart,
  Printer,
  ChevronLeft,
  ChevronRight,
  Users,
  DollarSign,
  CheckCircle2,
  Clock,
  CalendarDays,
  BarChart3,
  Calendar,
  Download,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';

const ProductionReportPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const getWeekStart = () => format(startOfWeek(new Date(), { weekStartsOn: 6 }), 'yyyy-MM-dd');
  const getWeekEnd = () => format(endOfWeek(new Date(), { weekStartsOn: 6 }), 'yyyy-MM-dd');
  const getMonthStart = () => format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const getMonthEnd = () => format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState(getWeekStart());
  const [endDate, setEndDate] = useState(getWeekEnd());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('daily'); // daily, weekly, or monthly
  
  // Password protection state
  const [productionUnlocked, setProductionUnlocked] = useState(false);
  const [hasProductionPassword, setHasProductionPassword] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { 
    fetchReport();
    checkProductionPassword();
  }, [startDate, endDate]);

  const checkProductionPassword = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/app-settings`, { headers: getAuthHeader() });
      const hasPassword = res.data.production_password && res.data.production_password.length > 0;
      setHasProductionPassword(hasPassword);
      if (!hasPassword) {
        setProductionUnlocked(true);
      }
    } catch (error) {
      setProductionUnlocked(true); // Default to unlocked if settings fail
    }
  };

  const handleUnlockProduction = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/verify-production-password`, 
        { password: passwordInput },
        { headers: getAuthHeader() }
      );
      if (res.data.valid) {
        setProductionUnlocked(true);
        setPasswordDialog(false);
        setPasswordInput('');
        toast.success('تم فتح القفل بنجاح');
      } else {
        toast.error('كلمة المرور غير صحيحة');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  // Update dates when view changes
  useEffect(() => {
    if (view === 'monthly') {
      setStartDate(getMonthStart());
      setEndDate(getMonthEnd());
    } else if (view === 'weekly') {
      setStartDate(getWeekStart());
      setEndDate(getWeekEnd());
    }
  }, [view]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/production-summary`, {
        params: { start_date: startDate, end_date: endDate },
        headers: getAuthHeader()
      });
      setReport(response.data);
    } catch (error) {
      console.error(error);
      toast.error(t('loadError') || 'خطأ');
    } finally {
      setLoading(false);
    }
  };

  const shiftPeriod = (dir) => {
    const s = new Date(startDate + 'T12:00:00');
    const e = new Date(endDate + 'T12:00:00');
    const days = view === 'monthly' ? 30 : 7;
    s.setDate(s.getDate() + dir * days);
    e.setDate(e.getDate() + dir * days);
    setStartDate(format(s, 'yyyy-MM-dd'));
    setEndDate(format(e, 'yyyy-MM-dd'));
  };

  const fc = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  // Print report function
  const handlePrint = () => {
    if (!report) return;
    
    const reportTitle = view === 'daily' ? 'تقرير الإنتاج اليومي' : view === 'weekly' ? 'تقرير الإنتاج الأسبوعي' : 'تقرير الإنتاج الشهري';
    
    let contentHTML = '';
    
    if (view === 'daily' && report.daily_data) {
      contentHTML = report.daily_data.map(day => `
        <div style="margin-bottom: 6mm; border: 1px solid #d6d3d1; border-radius: 2mm; overflow: hidden; page-break-inside: avoid;">
          <div style="background: #1e293b; color: white; padding: 3mm; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: bold;">${day.date} (${day.day_name})</span>
            <span style="color: #fcd34d; font-weight: bold;">${fc(day.sales_amount)} €</span>
          </div>
          <div style="padding: 3mm;">
            <h4 style="font-size: 10pt; color: #92400e; margin: 0 0 2mm 0;">الإنتاج:</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 2mm;">
              ${report.products.map(prod => {
                const p = day.production?.[prod.id];
                if (!p) return '';
                return `<span style="background: #fef3c7; padding: 1mm 3mm; border-radius: 1mm; font-size: 9pt;">${prod.name}: ${p.qty || 0} صندوق (${p.breads || 0} ربطة)</span>`;
              }).join('')}
            </div>
            <h4 style="font-size: 10pt; color: #166534; margin: 3mm 0 2mm 0;">المبيعات:</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 2mm;">
              ${report.products.map(prod => {
                const s = day.sales?.[prod.id];
                if (!s) return '';
                return `<span style="background: #dcfce7; padding: 1mm 3mm; border-radius: 1mm; font-size: 9pt;">${prod.name}: ${s.qty || 0} صندوق = ${fc(s.amount || 0)} €</span>`;
              }).join('')}
            </div>
          </div>
        </div>
      `).join('');
    } else {
      // Weekly/Monthly - Production Summary
      let productionHTML = '';
      if (report.weekly_production && Object.keys(report.weekly_production).length > 0) {
        productionHTML = `
          <h3 style="font-size: 12pt; color: #92400e; margin: 4mm 0 2mm 0; background: #fef3c7; padding: 2mm; border-radius: 1mm;">ملخص الإنتاج</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 5mm;">
            <thead>
              <tr style="background: #fef3c7;">
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">المنتج</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">الإنتاج (صندوق)</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">الربطات</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">المباع (صندوق)</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">المتبقي</th>
              </tr>
            </thead>
            <tbody>
              ${report.products.map(prod => {
                const produced = report.weekly_production?.[prod.id] || {};
                const sold = report.weekly_sold?.[prod.id] || {};
                const remQty = (produced.qty || 0) - (sold.qty || 0);
                return `
                  <tr>
                    <td style="border: 1px solid #d6d3d1; padding: 2mm;">${prod.name}</td>
                    <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; font-weight: bold; color: #92400e;">${produced.qty || 0}</td>
                    <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">${produced.breads || 0}</td>
                    <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; font-weight: bold; color: #1d4ed8;">${sold.qty || 0}</td>
                    <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; font-weight: bold; color: ${remQty >= 0 ? '#059669' : '#dc2626'};">${remQty}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        `;
      }

      // Customer Sales Table
      let customersHTML = '';
      if (report.customers && report.customers.length > 0) {
        customersHTML = `
          <h3 style="font-size: 12pt; color: #1d4ed8; margin: 4mm 0 2mm 0; background: #dbeafe; padding: 2mm; border-radius: 1mm;">المبيعات حسب الزبون</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 8pt; margin-bottom: 5mm;">
            <thead>
              <tr style="background: #dbeafe;">
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الزبون</th>
                ${report.products.map(p => `<th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">${p.name}</th>`).join('')}
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">المبيعات</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; color: #059669;">المدفوع</th>
                <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; color: #dc2626;">المستحق</th>
              </tr>
            </thead>
            <tbody>
              ${report.customers.map(cust => `
                <tr>
                  <td style="border: 1px solid #d6d3d1; padding: 2mm; font-weight: bold;">${cust.customer_name}</td>
                  ${report.products.map(p => {
                    const item = cust.items?.[p.id];
                    return `<td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center;">${item ? item.qty : '-'}</td>`;
                  }).join('')}
                  <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; font-weight: bold;">${fc(cust.total_sales || 0)} €</td>
                  <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; color: #059669;">${fc(cust.paid || 0)} €</td>
                  <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: center; color: #dc2626; font-weight: bold;">${fc(cust.outstanding || 0)} €</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      }

      // Summary
      contentHTML = `
        <div style="display: flex; justify-content: space-around; margin-bottom: 5mm; text-align: center;">
          <div style="background: #dbeafe; padding: 3mm; border-radius: 2mm;">
            <p style="font-size: 9pt; color: #1d4ed8; margin: 0;">إجمالي المبيعات</p>
            <p style="font-size: 14pt; font-weight: bold; color: #1d4ed8; margin: 1mm 0 0 0;">${fc(report.grand_sales || 0)} €</p>
          </div>
          <div style="background: #dcfce7; padding: 3mm; border-radius: 2mm;">
            <p style="font-size: 9pt; color: #059669; margin: 0;">المستلم</p>
            <p style="font-size: 14pt; font-weight: bold; color: #059669; margin: 1mm 0 0 0;">${fc(report.grand_paid || 0)} €</p>
          </div>
          <div style="background: #fee2e2; padding: 3mm; border-radius: 2mm;">
            <p style="font-size: 9pt; color: #dc2626; margin: 0;">المستحق</p>
            <p style="font-size: 14pt; font-weight: bold; color: #dc2626; margin: 1mm 0 0 0;">${fc(report.grand_outstanding || 0)} €</p>
          </div>
          <div style="background: #fef3c7; padding: 3mm; border-radius: 2mm;">
            <p style="font-size: 9pt; color: #92400e; margin: 0;">عدد الزبائن</p>
            <p style="font-size: 14pt; font-weight: bold; color: #92400e; margin: 1mm 0 0 0;">${report.customers?.length || 0}</p>
          </div>
        </div>
        ${productionHTML}
        ${customersHTML}
      `;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          @page { size: A4 portrait; margin: 10mm 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { font-family: Arial, sans-serif; font-size: 10pt; direction: rtl; padding: 5mm; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 6mm; border-bottom: 2px solid #2563eb; padding-bottom: 4mm;">
          <h2 style="font-size: 18pt; font-weight: bold; margin: 0 0 2mm 0; color: #1e293b;">${reportTitle}</h2>
          <p style="font-size: 10pt; color: #57534e; margin: 0;">${report.start_date || startDate} — ${report.end_date || endDate}</p>
        </div>
        ${contentHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Download PDF function
  const handleDownloadPDF = () => {
    if (!report) return;
    
    const reportTitle = view === 'daily' ? 'تقرير الإنتاج اليومي' : view === 'weekly' ? 'تقرير الإنتاج الأسبوعي' : 'تقرير الإنتاج الشهري';
    
    // Use same content as print
    handlePrint();
    toast.info('اختر "حفظ كـ PDF" من نافذة الطباعة');
  };

  // Helper: render qty + breads
  const QtyBreads = ({ qty, breads, color = 'text-stone-900' }) => {
    if (!qty) return <span className="text-stone-300">-</span>;
    return (
      <div className={`${color} text-center`}>
        <span className="font-bold text-base">{qty}</span>
        <span className="text-xs text-stone-500 block">{breads} {t('bundle') || 'ربطة'}</span>
      </div>
    );
  };

  return (
    <div data-testid="production-report-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 no-print">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <FileBarChart className="w-6 h-6 text-blue-600" />
          {t('productionReport') || 'تقرير الإنتاج'}
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={view === 'daily' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('daily')}
            className="gap-1"
          >
            <CalendarDays className="w-4 h-4" /> {t('daily') || 'يومي'}
          </Button>
          <Button
            variant={view === 'weekly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('weekly')}
            className="gap-1"
          >
            <BarChart3 className="w-4 h-4" /> {t('weekly') || 'أسبوعي'}
          </Button>
          <Button
            variant={view === 'monthly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('monthly')}
            className="gap-1 bg-purple-600 hover:bg-purple-700 text-white"
          >
            <BarChart3 className="w-4 h-4" /> {t('monthly') || 'شهري'}
          </Button>
          {view === 'daily' && (
            <>
              <Button onClick={handlePrint} variant="outline" size="sm" className="gap-1">
                <Printer className="w-4 h-4" /> {t('print') || 'طباعة'}
              </Button>
              <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50">
                <Download className="w-4 h-4" /> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Date Range */}
      <Card className="border-stone-200 no-print">
        <CardContent className="p-3">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => shiftPeriod(isRTL ? 1 : -1)} className="h-8 w-8 p-0">
              {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </Button>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36 text-center text-sm" />
            <span className="text-stone-400">—</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36 text-center text-sm" />
            <Button variant="ghost" size="sm" onClick={() => shiftPeriod(isRTL ? -1 : 1)} className="h-8 w-8 p-0">
              {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      ) : report ? (
        <>
          {/* Print Header */}
          <div className="hidden print:block mb-4" style={{textAlign: 'center', borderBottom: '3px solid #d97706', paddingBottom: '10px'}}>
            <h1 style={{fontSize: '20px', fontWeight: 'bold'}}>{user?.company_info?.company_name || 'المخبز'}</h1>
            <p style={{fontSize: '14px', fontWeight: 'bold', marginTop: '4px'}}>
              {view === 'daily' ? (t('dailyProductionDetail') || 'تقرير الإنتاج اليومي') : (t('productionReport') || 'تقرير الإنتاج الأسبوعي')}
            </p>
            <p style={{fontSize: '12px', color: '#666'}}>{report.start_date} — {report.end_date}</p>
          </div>

          {/* ==================== DAILY VIEW ==================== */}
          {view === 'daily' && report.daily_data?.map((day, dayIdx) => (
            <div key={dayIdx} className="print-customer-page">
              {/* Day Header */}
              <div className="bg-slate-800 text-white rounded-t-xl px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-amber-400" />
                  <span className="font-bold text-lg">{day.date}</span>
                  <span className="text-slate-300">({day.day_name})</span>
                </div>
                <span className="text-amber-300 font-bold">{fc(day.sales_amount)} €</span>
              </div>

              <div className="border border-t-0 border-stone-200 rounded-b-xl overflow-hidden">
                {/* Production Section */}
                <div className="bg-amber-50 p-4 border-b border-amber-200">
                  <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2 justify-between">
                    <span className="flex items-center gap-2">
                      <Factory className="w-4 h-4" />
                      {t('dailyProduction') || 'الإنتاج'}
                    </span>
                    {hasProductionPassword && !productionUnlocked && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="gap-1 text-amber-700 border-amber-400"
                        onClick={() => setPasswordDialog(true)}
                      >
                        <Lock className="w-3 h-3" /> فتح القفل
                      </Button>
                    )}
                  </h3>
                  {productionUnlocked ? (
                    Object.keys(day.production).length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {report.products.map(prod => {
                          const p = day.production[prod.id];
                          if (!p) return null;
                          return (
                            <div key={prod.id} className="bg-white rounded-lg border border-amber-200 p-3 text-center">
                              <p className="text-xs text-amber-600 mb-1">{prod.name}</p>
                              <p className="text-xl font-bold text-amber-800">{p.qty} <span className="text-xs font-normal">{t('box') || 'صندوق'}</span></p>
                              <p className="text-sm text-amber-600 font-semibold">{p.breads} {t('bundle') || 'ربطة'}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-500 italic">{t('noProductionToday') || 'لا يوجد إنتاج مسجل'}</p>
                    )
                  ) : (
                    <div className="bg-amber-100 border-2 border-dashed border-amber-300 rounded-lg p-6 text-center">
                      <Lock className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                      <p className="text-amber-700 font-medium">محتوى محمي بكلمة مرور</p>
                      <Button 
                        size="sm" 
                        className="mt-3 bg-amber-600 hover:bg-amber-700"
                        onClick={() => setPasswordDialog(true)}
                      >
                        إدخال كلمة المرور
                      </Button>
                    </div>
                  )}
                  {productionUnlocked && day.notes && <p className="mt-2 text-xs text-amber-600">{day.notes}</p>}
                </div>

                {/* Sold Totals Row */}
                {Object.keys(day.sold_totals).length > 0 && (
                  <div className="bg-blue-50 px-4 py-2 border-b border-blue-200 flex flex-wrap gap-4 items-center">
                    <span className="text-xs font-bold text-blue-800">{t('totalSold') || 'إجمالي المباع'}:</span>
                    {report.products.map(prod => {
                      const s = day.sold_totals[prod.id];
                      if (!s) return null;
                      return (
                        <span key={prod.id} className="text-sm text-blue-700">
                          {prod.name}: <strong>{s.qty}</strong> ({s.breads} {t('bundle') || 'ربطة'})
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Sales Table */}
                {day.customers.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-100 text-stone-700">
                        <th className="p-2 text-right font-semibold">{t('customerName') || 'الزبون'}</th>
                        {report.products.map(prod => (
                          <th key={prod.id} className="p-2 text-center font-semibold">{prod.name}</th>
                        ))}
                        <th className="p-2 text-center font-semibold">{t('total') || 'المبلغ'}</th>
                        <th className="p-2 text-center font-semibold w-16 no-print">{t('status') || 'الحالة'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.customers.map((cust, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                          <td className="p-2 font-medium">{cust.customer_name}</td>
                          {report.products.map(prod => {
                            const item = cust.items[prod.id];
                            return (
                              <td key={prod.id} className="p-2 text-center">
                                {item ? (
                                  <div>
                                    <span className="font-bold">{item.qty}</span>
                                    <span className="text-xs text-stone-400 block">{item.breads} {t('bundle') || 'ربطة'}</span>
                                  </div>
                                ) : <span className="text-stone-300">-</span>}
                              </td>
                            );
                          })}
                          <td className="p-2 text-center font-bold text-blue-700">{fc(cust.total)} €</td>
                          <td className="p-2 text-center no-print">
                            {cust.delivered ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                            ) : (
                              <Clock className="w-4 h-4 text-amber-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-center text-stone-400 py-4 text-sm">{t('noOrders') || 'لا توجد طلبات'}</p>
                )}
              </div>
            </div>
          ))}

          {/* ==================== WEEKLY VIEW ==================== */}
          {(view === 'weekly' || view === 'monthly') && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-blue-600">{t('totalSales') || 'إجمالي المبيعات'}</p>
                    <p className="text-lg font-bold text-blue-700">{fc(report.grand_sales)} €</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-emerald-600">{t('totalReceived') || 'المستلم'}</p>
                    <p className="text-lg font-bold text-emerald-700">{fc(report.grand_paid)} €</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-red-600">{t('totalOutstanding') || 'المستحق'}</p>
                    <p className="text-lg font-bold text-red-700">{fc(report.grand_outstanding)} €</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-amber-600">{t('customersCount') || 'عدد الزبائن'}</p>
                    <p className="text-lg font-bold text-amber-700">{report.customers?.length || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Period Label for Monthly View */}
              {view === 'monthly' && (
                <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-center">
                  <span className="text-purple-800 font-bold text-lg">
                    📅 {t('monthlyReport') || 'التقرير الشهري'}: {startDate} — {endDate}
                  </span>
                </div>
              )}

              {/* Weekly/Monthly Production Totals */}
              {Object.keys(report.weekly_production || {}).length > 0 && (
                <Card className="border-amber-300">
                  <CardHeader className="pb-2 bg-amber-50 rounded-t-lg">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Factory className="w-5 h-5 text-amber-600" />
                      {t('productionSummaryTitle') || 'ملخص الإنتاج الأسبوعي'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-amber-100">
                          <th className="p-3 text-right font-bold">{t('product') || 'المنتج'}</th>
                          <th className="p-3 text-center font-bold">{t('produced') || 'الإنتاج'}</th>
                          <th className="p-3 text-center font-bold">{t('bundles') || 'الربطات'}</th>
                          <th className="p-3 text-center font-bold">{t('sold') || 'المباع'}</th>
                          <th className="p-3 text-center font-bold">{t('bundlesSold') || 'ربطات مباعة'}</th>
                          <th className="p-3 text-center font-bold">{t('remaining') || 'المتبقي'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.products.map((prod, idx) => {
                          const produced = report.weekly_production[prod.id];
                          const sold = report.weekly_sold?.[prod.id];
                          const remQty = (produced?.qty || 0) - (sold?.qty || 0);
                          const remBreads = (produced?.breads || 0) - (sold?.breads || 0);
                          return (
                            <tr key={prod.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/50'}>
                              <td className="p-3 font-medium">{prod.name}</td>
                              <td className="p-3 text-center font-bold text-amber-700">{produced?.qty || 0}</td>
                              <td className="p-3 text-center text-amber-600">{produced?.breads || 0}</td>
                              <td className="p-3 text-center font-bold text-blue-700">{sold?.qty || 0}</td>
                              <td className="p-3 text-center text-blue-600">{sold?.breads || 0}</td>
                              <td className="p-3 text-center font-bold" style={{color: remQty >= 0 ? '#059669' : '#dc2626'}}>
                                {remQty} ({remBreads})
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Customer Sales Table */}
              {report.customers?.length > 0 && (
                <Card className="border-blue-300">
                  <CardHeader className="pb-2 bg-blue-50 rounded-t-lg">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      {t('customerSalesReport') || 'المبيعات حسب الزبون'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-blue-100 text-blue-900">
                            <th className="p-2 text-right font-bold min-w-[100px]">{t('customerName') || 'الزبون'}</th>
                            {report.products.map(p => (
                              <th key={p.id} className="p-2 text-center font-bold">{p.name}</th>
                            ))}
                            <th className="p-2 text-center font-bold">{t('totalSales') || 'المبيعات'}</th>
                            <th className="p-2 text-center font-bold text-emerald-800">{t('paid') || 'المدفوع'}</th>
                            <th className="p-2 text-center font-bold text-red-800">{t('outstanding') || 'المستحق'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.customers.map((cust, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                              <td className="p-2 font-medium">{cust.customer_name}</td>
                              {report.products.map(p => {
                                const qty = cust.items_sold?.[p.id] || 0;
                                const breads = qty * p.breads_per_box;
                                return (
                                  <td key={p.id} className="p-2 text-center">
                                    {qty > 0 ? (
                                      <div>
                                        <span className="font-bold">{qty}</span>
                                        <span className="text-xs text-stone-400 block">{breads} {t('bundle') || 'ربطة'}</span>
                                      </div>
                                    ) : <span className="text-stone-300">-</span>}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-bold text-blue-700">{fc(cust.total_sales)} €</td>
                              <td className="p-2 text-center font-bold text-emerald-600">{fc(cust.total_paid)} €</td>
                              <td className="p-2 text-center font-bold" style={{color: cust.outstanding > 0 ? '#dc2626' : '#059669'}}>
                                {fc(cust.outstanding)} €
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-800 text-white font-bold">
                            <td className="p-3">{t('total') || 'الإجمالي'}</td>
                            {report.products.map(p => {
                              const total = report.customers.reduce((s, c) => s + (c.items_sold?.[p.id] || 0), 0);
                              const breads = total * p.breads_per_box;
                              return (
                                <td key={p.id} className="p-3 text-center">
                                  {total} <span className="text-xs opacity-70">({breads})</span>
                                </td>
                              );
                            })}
                            <td className="p-3 text-center">{fc(report.grand_sales)} €</td>
                            <td className="p-3 text-center text-emerald-300">{fc(report.grand_paid)} €</td>
                            <td className="p-3 text-center text-red-300">{fc(report.grand_outstanding)} €</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* No Data */}
          {!report.daily_data?.length && (
            <Card className="border-stone-200">
              <CardContent className="p-12 text-center">
                <FileBarChart className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">{t('noData') || 'لا توجد بيانات في هذه الفترة'}</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 0.8cm; }
          body { font-size: 10px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { font-size: 10px; }
          td, th { padding: 4px 6px !important; }
          .print-customer-page { page-break-after: always; }
          .print-customer-page:last-child { page-break-after: auto; }
        }
      `}</style>

      {/* Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              إدخال كلمة المرور
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-stone-600">أدخل كلمة المرور لعرض كميات الإنتاج</p>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="كلمة المرور"
                onKeyDown={(e) => e.key === 'Enter' && handleUnlockProduction()}
              />
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(false)}>إلغاء</Button>
            <Button onClick={handleUnlockProduction} className="bg-amber-600 hover:bg-amber-700">
              فتح القفل
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionReportPage;
